// The RBP add to cart's two dialogs, SL1.0's confirm and done modals
// (`rbp_npr_confirm_add_modal.htm`, `rbp_npr_add_done_modal.htm`) on the design-system `Modal`:
//   - the confirmation, "Add RBP items to Cart(s)?", with a Depot / RBP items row per depot that
//     has a line and the actions "Add items to cart(s)" and "Cancel";
//   - the done step, "RBP Items successfully added to Cart(s)", with the depots written, the
//     selections that could not be added and stay selected, an "Open cart" link per depot and
//     "Back to RBP Shopping List".
// SL1.0's done modal linked each depot's cart and switched the session's depot on the way; here
// "Open cart" goes to the depot chooser, one tap from each cart, so adding never changes the active
// depot behind the person's back.
import { Link } from 'react-router';
import type { RbpCartAdditionDepot } from '../../api/rbp.client';
import { Banner, Button, Modal } from '../../design-system';
import { CHOOSE_ROUTE } from '../../ordering/orderingChoices';
import type { RbpCartAdditionState } from '../../rbp/useRbpCartAddition';
import styles from './RbpCartAdditionDialog.module.css';

function DepotTable({
  caption,
  countHeading,
  depots,
  openCartLinks,
}: {
  readonly caption: string;
  readonly countHeading: string;
  readonly depots: ReadonlyArray<RbpCartAdditionDepot & { readonly addedCount?: number }>;
  readonly openCartLinks: boolean;
}) {
  return (
    <table className={styles.table}>
      <caption className={styles.caption}>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Depot</th>
          <th scope="col" className={styles.count}>
            {countHeading}
          </th>
          {openCartLinks ? (
            <th scope="col">
              <span className={styles.visuallyHidden}>Cart</span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {depots.map((depot) => (
          <tr key={depot.assortmentCode} data-assortment-code={depot.assortmentCode}>
            <th scope="row" className={styles.depot}>
              {depot.displayName}
            </th>
            <td className={styles.count}>{depot.addedCount ?? depot.lineCount}</td>
            {openCartLinks ? (
              <td className={styles.link}>
                <Link to={CHOOSE_ROUTE} aria-label={`Open cart, ${depot.displayName}`}>
                  Open cart
                </Link>
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RbpCartAdditionDialog({
  state,
  onConfirm,
  onClose,
}: {
  readonly state: RbpCartAdditionState;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  if (state.status === 'confirm' || state.status === 'adding') {
    const adding = state.status === 'adding';
    return (
      <Modal
        open
        // While the add runs the dialog stays: closing it would not stop the write.
        onClose={adding ? () => undefined : onClose}
        title="Add RBP items to Cart(s)?"
        description="Here is a breakdown of items to be added across your depot carts."
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={adding}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onConfirm} disabled={adding} aria-busy={adding}>
              {adding ? 'Adding…' : 'Add items to cart(s)'}
            </Button>
          </>
        }
      >
        <DepotTable
          caption="Items to add per depot"
          countHeading="RBP items"
          depots={state.preview.depots}
          openCartLinks={false}
        />
      </Modal>
    );
  }
  if (state.status === 'done') {
    const { result } = state;
    return (
      <Modal
        open
        onClose={onClose}
        title="RBP Items successfully added to Cart(s)"
        description="Please complete all orders within depot pages."
        footer={
          <Button variant="primary" onClick={onClose}>
            Back to RBP Shopping List
          </Button>
        }
      >
        <DepotTable
          caption="Items added per depot"
          countHeading="Items added by RBP"
          depots={result.depots}
          openCartLinks
        />
        {result.selectionsCleared ? null : (
          // The carts took the lines but the list could not clear them: a second add would add
          // the quantities again.
          <div className={styles.warning}>
            <Banner type="warning">
              These products are still selected on your list. Clear the selection before adding
              again, or their quantities will be added twice.
            </Banner>
          </div>
        )}
        {result.notAddedCount > 0 ? (
          <p className={styles.notAdded} data-not-added>
            {result.notAddedCount === 1
              ? '1 selected product could not be added and stays selected.'
              : `${result.notAddedCount} selected products could not be added and stay selected.`}
          </p>
        ) : null}
      </Modal>
    );
  }
  return null;
}
